import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useCultivationFormFieldData } from "../../../../api/farmGate";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import { Form } from "../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormTextarea } from "../../../../components/ui/Input/FormTextArea/FormTextarea";
import {
  CultivationRecord,
  Fertilizer,
  FuelUse,
  LandUsageChange,
  Pesticide
} from "../../../../types/prodcut-data-review/product-data.types";
import { CULTIVATION_DATA_TYPE_OPTIONS } from "./constants/cultivationEditor.constants";
import { useCultivationEditorOptions } from "./hooks/useCultivationEditorOptions";
import { useDraftList } from "./hooks/useDraftList";
import {
  buildCultivationFieldPatch,
  buildFertilizerPayload,
  buildFuelUsePayload,
  buildLandUsagePayload,
  buildPesticidePayload,
  createFertilizer,
  createFuelUse,
  createLandUsageChange,
  createPesticide,
  lookupText,
  mainFieldDefaults
} from "./utils/cultivationEditor.utils";
import FertilizersSection from "./components/FertilizersSection";
import FuelUseSection from "./components/FuelUseSection";
import LandUseChangeSection from "./components/LandUseChangeSection";
import PesticidesSection from "./components/PesticidesSection";
import CultivationRowActions from "./components/CultivationRowActions";

interface CultivationEditorProps {
  rec: CultivationRecord | undefined;
  onChange: (patch: Partial<CultivationRecord>) => void;
  onSaved?: () => void;
  disabled?: boolean;
}

const CultivationEditor: React.FC<CultivationEditorProps> = ({ rec, onChange, onSaved, disabled = false }) => {
  const { fertilizerList, pesticideList, energySources, produceTypes, countries } =
    useCultivationEditorOptions();

  const cf = rec?.cultivationFields;
  const [editing, setEditing] = useState(false);

  // React Hook Form for main cultivation fields — values are read at Save time,
  // not pushed to the parent on every keystroke
  const cultivationForm = useForm({
    defaultValues: mainFieldDefaults(rec),
    mode: "onChange"
  });

  // Draft copies of the four list sections. While not editing, they mirror the
  // last-saved data from the parent; entering edit mode freezes them so Add/Update/
  // Remove only mutate the local draft until Save persists everything at once.
  const fertilizers = useDraftList<Fertilizer>(cf?.fertilizerList ?? [], editing);
  const pesticides = useDraftList<Pesticide>(cf?.pesticideList ?? [], editing);
  const fuelUses = useDraftList<FuelUse>(cf?.fuelUse ?? [], editing);
  const landUsage = useDraftList<LandUsageChange>(cf?.landUsage ?? [], editing);

  const { mutate: saveCultivationField, isLoading: isSaving } = useCultivationFormFieldData({
    onSuccess: () => {
      onChange(
        buildCultivationFieldPatch(cultivationForm.getValues(), cf, {
          fertilizerList: fertilizers.draft,
          pesticideList: pesticides.draft,
          fuelUse: fuelUses.draft,
          landUsage: landUsage.draft
        })
      );
      successMessage("Cultivation updated");
      onSaved?.();
      setEditing(false);
    },
    onError: (error: any) => errorView(error?.msg || "Failed to save cultivation data")
  });

  const handleSaveRow = () => {
    const values = cultivationForm.getValues();

    saveCultivationField({
      farmGateId: rec?.id,
      yield: values.yieldAmount !== "" ? Number(values.yieldAmount) : undefined,
      growthArea: values.area !== "" ? Number(values.area) : undefined,
      harvestYear: values.harvestYear,
      harvestSeason: cf?.harvestSeason,
      produceCultivar: values.cropName,
      fertilizerList: buildFertilizerPayload(fertilizers.draft),
      pesticideList: buildPesticidePayload(pesticides.draft),
      irrigations: cf?.irrigations ?? [],
      fuelUse: buildFuelUsePayload(fuelUses.draft),
      landUsage: buildLandUsagePayload(landUsage.draft)
    });
  };

  const handleCancelRow = () => {
    cultivationForm.reset(mainFieldDefaults(rec));
    setEditing(false);
  };

  const headerCls = "tw-bg-primary/10 tw-text-primary tw-font-medium tw-text-xs";
  const subSectionHeader =
    "tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-bg-primary/5 tw-border-b";

  if (!rec) return null;

  return (
    <div className="tw-border tw-rounded-md tw-bg-background tw-overflow-hidden">
      {/* Main cultivation data table */}
      <Form {...cultivationForm}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={headerCls}>Cultivation Name</TableHead>
              <TableHead className={headerCls}>Data Type</TableHead>
              <TableHead className={headerCls}>Product Type</TableHead>
              <TableHead className={headerCls}>Original Country</TableHead>
              <TableHead className={headerCls}>Harvest Year</TableHead>
              <TableHead className={headerCls}>Growth Area (ha)</TableHead>
              <TableHead className={headerCls}>Harvest Yield</TableHead>
              <TableHead className={`${headerCls} tw-text-right tw-w-[90px]`}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormInput control={cultivationForm.control} name="cropName" label="" />
                ) : (
                  <span className="tw-text-sm">{rec.cropName || "—"}</span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormSelect
                    control={cultivationForm.control}
                    name="dataType"
                    label=""
                    placeholder="Select"
                    options={CULTIVATION_DATA_TYPE_OPTIONS}
                  />
                ) : (
                  <span className="tw-text-sm">{rec.dataType || "—"}</span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormSelect
                    control={cultivationForm.control}
                    name="produceType"
                    label=""
                    placeholder="Select"
                    options={produceTypes}
                  />
                ) : (
                  <span className="tw-text-sm">
                    {rec.produceType !== "" && rec.produceType != null
                      ? lookupText(produceTypes, rec.produceType)
                      : "—"}
                  </span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormSelect
                    control={cultivationForm.control}
                    name="originCountry"
                    label=""
                    placeholder="Select"
                    options={countries}
                  />
                ) : (
                  <span className="tw-text-sm">
                    {rec.originCountry !== "" && rec.originCountry != null
                      ? lookupText(countries, rec.originCountry)
                      : "—"}
                  </span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormInputNumber
                    control={cultivationForm.control}
                    name="harvestYear"
                    label=""
                    isYearInput
                    placeholder="Year"
                  />
                ) : (
                  <span className="tw-text-sm tw-font-mono">{rec.harvestYear || "—"}</span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormInputNumber control={cultivationForm.control} name="area" label="" />
                ) : (
                  <span className="tw-text-sm tw-font-mono">{rec.area || "—"}</span>
                )}
              </TableCell>
              <TableCell className="tw-align-top [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                {editing ? (
                  <FormInputNumber
                    control={cultivationForm.control}
                    name="yieldAmount"
                    label=""
                    rightContentEnable
                    rightContent={rec.yieldUnit}
                  />
                ) : (
                  <span className="tw-text-sm tw-font-mono">
                    {rec.yieldAmount !== "" ? `${rec.yieldAmount} ${rec.yieldUnit}` : "—"}
                  </span>
                )}
              </TableCell>
              <TableCell className="tw-text-right tw-align-top">
                <CultivationRowActions
                  editing={editing}
                  saving={isSaving}
                  onEdit={() => setEditing(true)}
                  onSave={handleSaveRow}
                  onCancel={handleCancelRow}
                  disabled={disabled}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Fertilizers sub-section */}
        <FertilizersSection
          fertilizers={fertilizers.draft}
          fertilizerList={fertilizerList}
          editing={editing}
          subSectionHeader={subSectionHeader}
          lookupText={lookupText}
          onAdd={() => fertilizers.add(createFertilizer())}
          onUpdate={fertilizers.update}
          onRemove={fertilizers.remove}
        />

        {/* Pesticides sub-section */}
        <PesticidesSection
          pesticides={pesticides.draft}
          pesticideList={pesticideList}
          editing={editing}
          subSectionHeader={subSectionHeader}
          lookupText={lookupText}
          onAdd={() => pesticides.add(createPesticide())}
          onUpdate={pesticides.update}
          onRemove={pesticides.remove}
        />

        {/* Land use change sub-section */}
        <LandUseChangeSection
          landUsage={landUsage.draft}
          editing={editing}
          subSectionHeader={subSectionHeader}
          onAdd={() => landUsage.add(createLandUsageChange())}
          onUpdate={landUsage.update}
          onRemove={landUsage.remove}
        />

        {/* Fuel use sub-section */}
        {/* <FuelUseSection
          fuelUses={fuelUses.draft}
          energySources={energySources}
          editing={editing}
          subSectionHeader={subSectionHeader}
          lookupText={lookupText}
          onAdd={() => fuelUses.add(createFuelUse())}
          onUpdate={fuelUses.update}
          onRemove={fuelUses.remove}
        /> */}

        {/* Notes */}
        <div className="tw-border-t tw-p-3">
          {editing ? (
            <FormTextarea control={cultivationForm.control} name="notes" label="Notes" rows={2} />
          ) : (
            <div>
              <label className="tw-text-xs tw-text-muted-foreground tw-mb-1 tw-block">Notes</label>
              <p className="tw-text-sm">{rec.notes || "—"}</p>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};

export default CultivationEditor;
